import type { CompanyRegistryCheckResult, KyckrCompanyProfile } from '@ballerine/common';

import type { BaseRegistryDataDisplayProps } from './RegistryDataDisplay';

export type DistrubutivePick<TPickFrom, TToPick extends PropertyKey> = TPickFrom extends any
  ? Pick<TPickFrom, Extract<keyof TPickFrom, TToPick>>
  : never;

export const RegistryInformationVendor = {
  KYCKR: 'KYCKR',
  OPEN_CORPORATES: 'OPEN_CORPORATES',
  ASIA_VERIFY: 'ASIA_VERIFY',
} as const;

type AdapterBuilder<TData, TRaw extends TData, TProcessed> = (data: TData) => {
  raw: TRaw;
  processed: TProcessed;
};

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- Interface is required for using 'this' in the adapter
export interface VendorRegistryInformationAdapter<
  TVendor extends keyof typeof RegistryInformationVendor,
  TData,
  TRaw extends TData,
> {
  vendor: TVendor;
  data: TData;
  raw: TRaw;
  processed: BaseRegistryDataDisplayProps['processed'];
  adapter: AdapterBuilder<this['data'], this['raw'], this['processed']>;
}

type KyckrVendorRegistryInformationAdapter = VendorRegistryInformationAdapter<
  typeof RegistryInformationVendor.KYCKR,
  KyckrCompanyProfile,
  KyckrCompanyProfile
>;

type OpenCorporatesVendorRegistryInformationAdapter = VendorRegistryInformationAdapter<
  typeof RegistryInformationVendor.OPEN_CORPORATES,
  CompanyRegistryCheckResult | null,
  CompanyRegistryCheckResult | null
>;

type AsiaVerifyVendorRegistryInformationAdapter = VendorRegistryInformationAdapter<
  typeof RegistryInformationVendor.ASIA_VERIFY,
  never,
  never
>;

export type VendorRegistryInformationAdapterUnion =
  | KyckrVendorRegistryInformationAdapter
  | OpenCorporatesVendorRegistryInformationAdapter
  | AsiaVerifyVendorRegistryInformationAdapter;

type RegistryInformationKyckrAdapter = KyckrVendorRegistryInformationAdapter['adapter'];

type RegistryInformationOpenCorporatesAdapter =
  OpenCorporatesVendorRegistryInformationAdapter['adapter'];

type RegistryInformationAsiaVerifyAdapter = AsiaVerifyVendorRegistryInformationAdapter['adapter'];

export type ExtractVendorRegistryInformationAdapterByVendor<
  TVendor extends VendorRegistryInformationAdapterUnion['vendor'],
> = Extract<VendorRegistryInformationAdapterUnion, { vendor: TVendor }>;

export type ExtractRegistryInformationAdapterRawByVendor<
  TVendor extends RegistryInformationAdapterVendor,
> = ReturnType<ExtractVendorRegistryInformationAdapterByVendor<TVendor>['adapter']>['raw'];

export const registryInformationKyckrAdapter = (data => {
  const individuals = data.representatives?.individuals ?? [];

  const activeOfficers = individuals
    .filter(individual => individual.isActive !== false)
    .map(individual => ({
      name: individual.name ?? '',
      position: individual.role?.normalized ?? individual.role?.original,
      address: individual.placeOfResidence?.fullAddress,
      startDate: individual.startDate?.normalized,
      endDate: individual.endDate?.normalized,
      isInactive: false,
    }));

  const inactiveOfficers = individuals
    .filter(individual => individual.isActive === false)
    .map(individual => ({
      name: individual.name ?? '',
      position: individual.role?.normalized ?? individual.role?.original,
      address: individual.placeOfResidence?.fullAddress,
      startDate: individual.startDate?.normalized,
      endDate: individual.endDate?.normalized,
      isInactive: true,
    }));

  const otherIdentifiers = data.identifiers?.otherIdentifiers ?? [];
  const identifiers = [
    data.identifiers?.taxNumber && {
      label: `Tax Number: ${data.identifiers.taxNumber}`,
      value: data.identifiers.taxNumber,
    },
    data.identifiers?.secondaryRegistrationNumber && {
      label: `Secondary Reg: ${data.identifiers.secondaryRegistrationNumber}`,
      value: data.identifiers.secondaryRegistrationNumber,
    },
    ...otherIdentifiers
      .filter(identifier => identifier.value)
      .map(identifier => ({
        label: `${identifier.type}: ${identifier.value}`,
        value: identifier.value ?? '',
      })),
  ].filter(Boolean) as Array<{ label: string; value: string }>;

  const industries = (data.activities ?? [])
    .filter(activity => activity.code)
    .map(activity => {
      const parts = [activity.code, activity.description].filter(Boolean);
      const label = activity.classificationScheme
        ? `${parts.join(' - ')} (${activity.classificationScheme})`
        : parts.join(' - ');

      return { label, value: activity.code ?? '' };
    });

  const previousNamesList = data.previousNames ?? [];
  const formattedPreviousNames = previousNamesList
    .filter(name => name.name)
    .map(name => ({
      label: name.startDate?.normalized
        ? `${name.name} (${name.startDate.normalized} - ${name.endDate?.normalized ?? 'present'})`
        : (name.name ?? ''),
      value: name.name ?? '',
    }));

  const aliasesList = data.aliases ?? [];
  const alternativeNames = [
    data.tradingName && {
      label: `${data.tradingName} (Trading Name)`,
      value: data.tradingName,
    },
    data.englishName && {
      label: `${data.englishName} (English Name)`,
      value: data.englishName,
    },
    ...aliasesList
      .filter(alias => alias.name)
      .map(alias => ({
        label: alias.type ? `${alias.name} (${alias.type})` : (alias.name ?? ''),
        value: alias.name ?? '',
      })),
  ].filter(Boolean) as Array<{ label: string; value: string }>;

  return {
    processed: {
      source: data.registrationAuthority,
      lastUpdatedAt: data.lastUpdated,
      companyName: data.companyName,
      companyNumber: data.identifiers?.primaryRegistrationNumber,
      jurisdictionCode: data.incorporationJurisdiction?.original,
      companyType: data.legalForm?.normalized ?? data.legalForm?.original,
      currentStatus: data.status?.normalized ?? data.status?.original,
      incorporationDate:
        data.incorporationDate?.normalized ??
        data.foundationDate?.normalized ??
        data.registrationDate?.normalized,
      dissolutionDate: data.dissolutionDate?.normalized,
      registeredAddress: data.addresses?.[0]?.fullAddress,
      agentName: data.registeredAgentName,
      identifiers,
      industries,
      previousNames: formattedPreviousNames,
      alternativeNames,
      activeOfficers,
      inactiveOfficers,
    },
    raw: data,
  };
}) satisfies RegistryInformationKyckrAdapter;

export const registryInformationOpenCorporatesAdapter = (data => {
  if (!data?.success || !data.data) {
    return {
      processed: {},
      raw: data,
    };
  }

  const registryData = data.data;

  const activeOfficers = registryData.officers
    .filter(officer => !officer.isInactive)
    .map(officer => ({
      name: officer.name,
      position: officer.position ?? undefined,
      address: officer.address ?? undefined,
      startDate: officer.startDate ?? undefined,
      endDate: officer.endDate ?? undefined,
      occupation: officer.occupation ?? undefined,
      isInactive: false,
    }));

  const inactiveOfficers = registryData.officers
    .filter(officer => officer.isInactive)
    .map(officer => ({
      name: officer.name,
      position: officer.position ?? undefined,
      address: officer.address ?? undefined,
      startDate: officer.startDate ?? undefined,
      endDate: officer.endDate ?? undefined,
      occupation: officer.occupation ?? undefined,
      isInactive: true,
    }));

  const identifiers = registryData.identifiers.map(identifier => ({
    label: `${identifier.identifierSystemName}: ${identifier.identifierSystemCode}`,
    value: identifier.identifierSystemCode,
  }));

  const industries = registryData.industries.map(industry => ({
    label: `${industry.code}${industry.description ? ` - ${industry.description}` : ''} (${industry.codeSchemeName})`,
    value: industry.code,
  }));

  const previousNames = registryData.previousNames.map(name => ({
    label: name.startDate
      ? `${name.companyName} (${name.startDate} - ${name.endDate ?? 'present'})`
      : name.companyName,
    value: name.companyName,
  }));

  const alternativeNames = registryData.alternativeNames.map(name => ({
    label: name.language
      ? `${name.companyName} (${name.language})`
      : name.type
        ? `${name.companyName} [${name.type}]`
        : name.companyName,
    value: name.companyName,
  }));

  const registeredAddress =
    registryData.registeredAddressInFull ??
    (registryData.registeredAddress
      ? [
          registryData.registeredAddress.streetAddress,
          registryData.registeredAddress.locality,
          registryData.registeredAddress.region,
          registryData.registeredAddress.postalCode,
          registryData.registeredAddress.country,
        ]
          .filter(Boolean)
          .join(', ')
      : undefined);

  return {
    processed: {
      source: 'OpenCorporates',
      sourceUrl: registryData.openCorporatesUrl ?? undefined,
      fetchedAt: data.retrievedAt,
      lastUpdatedAt: registryData.source.retrievedAt,
      companyName: registryData.name,
      companyNumber: registryData.companyNumber,
      jurisdictionCode: registryData.jurisdictionCode,
      companyType: registryData.companyType ?? undefined,
      currentStatus:
        registryData.currentStatus ?? (registryData.isInactive ? 'Inactive' : 'Active'),
      incorporationDate: registryData.incorporationDate ?? undefined,
      dissolutionDate: registryData.dissolutionDate ?? undefined,
      registeredAddress,
      agentName: registryData.agentName ?? undefined,
      numberOfEmployees: registryData.numberOfEmployees ?? undefined,
      isBranch: registryData.isBranch,
      branchStatus: registryData.branchStatus ?? undefined,
      identifiers,
      industries,
      previousNames,
      alternativeNames,
      activeOfficers,
      inactiveOfficers,
    },
    raw: data,
  };
}) satisfies RegistryInformationOpenCorporatesAdapter;

export const registryInformationAsiaVerifyAdapter = (_data => {
  throw new Error('Asia Verify is not implemented');
}) satisfies RegistryInformationAsiaVerifyAdapter;

export type RegistryInformationAdapter = (
  params: DistrubutivePick<VendorRegistryInformationAdapterUnion, 'vendor' | 'data'>,
) => DistrubutivePick<VendorRegistryInformationAdapterUnion, 'raw' | 'processed'>;

export type RegistryInformationAdapterParamaters = Parameters<typeof registryInformationAdapter>[0];

export type RegistryInformationAdapterVendor = RegistryInformationAdapterParamaters['vendor'];

export const registryInformationAdapter = (({ vendor, data }) => {
  switch (vendor) {
    case RegistryInformationVendor.KYCKR:
      return registryInformationKyckrAdapter(data);
    case RegistryInformationVendor.OPEN_CORPORATES:
      return registryInformationOpenCorporatesAdapter(data);
    case RegistryInformationVendor.ASIA_VERIFY:
      return registryInformationAsiaVerifyAdapter(data);
    default:
      vendor satisfies never;
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions -- runtime only protection
      throw new Error(`Invalid vendor ${vendor}`);
  }
}) satisfies RegistryInformationAdapter;

export const hasValue = <TValue>(value: TValue): value is NonNullable<TValue> => {
  if (value === null || value === undefined) {
    return false;
  }

  if (typeof value === 'string') {
    return value.trim().length > 0;
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (typeof value === 'object') {
    return Object.values(value).some(hasValue);
  }

  return true;
};
